import type {
  ConnectorConfig,
  ConnectorLogEntry,
  ConnectorLogLevel,
  ConnectorScope,
  ConnectorStatus,
  ConnectorType,
  SourceSensorBinding,
} from '@atnt/shared';

/**
 * In-browser store of the connectors mock: one record per organization (its organization-scope connectors, shared by
 * all its reservoirs) and one per tenant (its own connectors + the source sensors and log of every connector as seen
 * by this tenant). Kept in memory and mirrored to localStorage (best effort — private mode / blocked storage just
 * means the seed is rebuilt on reload).
 */

export interface ConnectorRecord {
  id: string;
  name: string;
  description: string | null;
  type: ConnectorType;
  scope: ConnectorScope;
  organizationId: string;
  tenantId: string | null;
  enabled: boolean;
  status: ConnectorStatus;
  lastSyncAt: string | null;
  lastError: string | null;
  /** full config including secrets (mock only — the real API never returns them) */
  config: ConnectorConfig;
  createdBy: { id: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface SensorRecord {
  id: string;
  node: string;
  columnName: string;
  channel: string | null;
  typeLabel: string | null;
  unit: string | null;
  lastValue: number | null;
  lastValueAt: string | null;
  binding: SourceSensorBinding | null;
}

export interface OrgStore {
  v: 1;
  connectors: ConnectorRecord[];
}

export interface TenantStore {
  v: 1;
  connectors: ConnectorRecord[];
  /** connectorId → sensors (for organization connectors: this tenant's site) */
  sensors: Record<string, SensorRecord[]>;
  logs: Record<string, ConnectorLogEntry[]>;
}

const PREFIX = 'atnt.connectors.mock.v1';
const memory = new Map<string, OrgStore | TenantStore>();

function load<T extends OrgStore | TenantStore>(key: string, seed: () => T): T {
  const hit = memory.get(key);
  if (hit) return hit as T;
  let value: T | null = null;
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as T) : null;
    if (parsed && parsed.v === 1) value = parsed;
  } catch {
    value = null;
  }
  const store = value ?? seed();
  memory.set(key, store);
  if (!value) persist(key, store);
  return store;
}

function persist(key: string, store: OrgStore | TenantStore) {
  memory.set(key, store);
  try {
    window.localStorage.setItem(key, JSON.stringify(store));
  } catch {
    /* storage unavailable — the in-memory copy still works for this session */
  }
}

export const orgKey = (organizationId: string) => `${PREFIX}:org:${organizationId}`;
export const tenantKey = (tenantId: string) => `${PREFIX}:tenant:${tenantId}`;

export const loadOrg = (organizationId: string, seed: () => OrgStore) => load(orgKey(organizationId), seed);
export const loadTenant = (tenantId: string, seed: () => TenantStore) => load(tenantKey(tenantId), seed);
export const saveOrg = (organizationId: string, store: OrgStore) => persist(orgKey(organizationId), store);
export const saveTenant = (tenantId: string, store: TenantStore) => persist(tenantKey(tenantId), store);

/* ───────────── deterministic helpers ───────────── */

/** FNV-1a 32-bit. */
export function hash32(text: string, seed = 0x811c9dc5) {
  let h = seed >>> 0;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Stable UUID-shaped id from a seed string (seeded records keep their ids — and URLs — across reloads). */
export function stableId(seed: string) {
  const hex = [0x811c9dc5, 0x1234567, 0x9e3779b9, 0x7f4a7c15].map((s) => hash32(seed, s).toString(16).padStart(8, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

/** mulberry32 PRNG. */
export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : stableId(`${Date.now()}-${Math.random()}`);

export function logEntry(level: ConnectorLogLevel, code: string, message: string, params?: Record<string, string | number>, at = new Date()): ConnectorLogEntry {
  return { id: newId(), at: at.toISOString(), level, code, message, params };
}
